// Backend origin resolution.
//
// The SPA (Cloudflare Pages, https://ygoevolution.com) and the world server (Cloudflare Worker, WS world
// protocol + card API) are DIFFERENT origins, so the backend URL is baked in at build time via
// VITE_WORLD_URL and every WS/API call is absolute. In dev VITE_WORLD_URL is unset → "" →
// same-origin relative URLs, which the Vite proxy forwards to the local world server (no CORS,
// first-party cookies).
//
// Set at build: `VITE_WORLD_URL=https://engine.ygoevolution.com pnpm build`.

const RAW = (import.meta.env.VITE_WORLD_URL ?? "").replace(/\/+$/, "");

/** Absolute HTTP origin of the backend, or "" for same-origin. */
export const API_BASE = RAW;

/** Absolute WS origin of the backend (derived from the HTTP origin). */
export const WS_BASE = RAW
  ? RAW.replace(/^http/, "ws")
  : `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}`;

/** Prefix a backend path (e.g. "/cards/search") with API_BASE; same-origin-safe when API_BASE is "". */
export const api = (path: string): string => `${API_BASE}${path}`;

// Auth is a Cloudflare Pages Function (better-auth + Discord OAuth + D1) exposed on its own custom
// domain https://api.ygoevolution.com under /api/*. The client opens /api/login in a popup. Defaults
// to the deployed auth origin so dev needs no wiring; override with VITE_AUTH_URL.
const AUTH_RAW = (import.meta.env.VITE_AUTH_URL ?? "").replace(/\/+$/, "");
export const AUTH_BASE = AUTH_RAW || "https://api.ygoevolution.com";

// Where the bulky game art (~1.1 GB of map tiles, chibi parts, sound) is loaded from. This is
// INTENTIONALLY decoupled from the API backend (VITE_WORLD_URL): the art is far too big to ship in
// the Worker bundle, so it lives in a Cloudflare R2 bucket and is fetched directly from there.
//   - prod → VITE_ASSET_BASE = the bucket's R2 *custom domain* (set at build). NOT the
//            pub-<hash>.r2.dev dev URL, that is rate-limited and 429s under the char creator's
//            burst of part-PNG requests; a custom domain routes through the CDN (no throttle).
//   - dev  → VITE_ASSET_BASE unset → `/assets/...` → Vite proxy → local world server (full tree)
const ASSET_RAW = (import.meta.env.VITE_ASSET_BASE ?? "").replace(/\/+$/, "");

/** Resolve a game-art path (e.g. "maps/.../x.png", "characters/disks/x.png") to its R2 (prod) or proxied (dev) URL. */
export const assetUrl = (path: string): string =>
  ASSET_RAW ? `${ASSET_RAW}/${path}` : `/assets/${path}`;

// Card art (by Konami passcode) is SELF-HOSTED in the R2 bucket under cardart/{large,small}/, see
// scripts/download-cardart.mjs + publish-cardart.sh. We stopped hotlinking images.ygoprodeck.com at
// runtime entirely (it rate-limits / 429s under the deck-builder + duel-board burst), so card art
// ALWAYS resolves to R2, in dev too. R2 serves over its CDN custom domain with GET * CORS, so it's
// WebGL-texture-safe cross-origin (no proxy needed). Defaults to the live host; in prod it tracks
// VITE_ASSET_BASE (same bucket); override either with VITE_CARD_BASE.
const CARD_RAW = (import.meta.env.VITE_CARD_BASE ?? "").replace(/\/+$/, "");
export const CARD_ART_BASE = CARD_RAW || ASSET_RAW || "https://assets.ygoevolution.com";

/** Self-hosted card image by passcode; big=full art, else 168px thumbnail. */
export const cardArtUrl = (id: number, big = false): string =>
  `${CARD_ART_BASE}/cardart/${big ? "large" : "small"}/${id}.jpg`;
